import React, { useState } from 'react'
import { AlertCircle, CheckCircle, Heart, Calendar, Award, Network } from 'lucide-react'
import { useNavigationStore } from '@/stores/navigation'
import DownloadForm from '@/components/common/DownloadForm'

const CaseStudyNLCHI: React.FC = () => {
  const navigationStore = useNavigationStore()
  const handleBookDemo = () => navigationStore.openDemoModal()
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  return (
    <main className="min-h-screen w-full bg-white text-slate-900">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-emerald-50/30 to-indigo-50" />
        <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-24">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <h1 className="text-3xl md:text-5xl font-semibold leading-tight">How Newfoundland & Labrador Streamlined Vascular Imaging Orders with EceptionistCX</h1>
              <p className="mt-4 text-lg md:text-xl text-slate-700">NLCHI expanded its long-standing use of Eceptionist telehealth services (since 2009) by adopting SchedulingCX to power real-time eOrdering and scheduling across the province—integrated with HEALTHe NL.</p>
              <div className="mt-8 flex gap-3">
                <button onClick={() => setDownloadFormOpen(true)} className="rounded-2xl px-6 py-3 bg-slate-900 text-white hover:bg-slate-800 transition">Download Full Case Study</button>
                <button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 transition">Book a Demo</button>
              </div>
            </div>
            <div className="h-64 md:h-80 rounded-3xl ring-1 ring-slate-200 p-6 flex items-center bg-white/90">
              <div className="w-full">
                <div className="text-lg md:text-xl font-semibold uppercase tracking-wide text-slate-700">Tools Used</div>
                <div className="mt-4 space-y-3">
                  <div className="leading-none">
                    <span className="lowercase font-normal text-4xl md:text-5xl text-[#0054A6]">scheduling</span>
                    <span className="font-bold uppercase text-4xl md:text-5xl text-[#7AC043]">CX</span>
                    <span className="align-top ml-1 text-xs md:text-sm font-normal text-slate-600">TM</span>
                  </div>
                </div>
                <div className="mt-4 text-sm text-slate-600">Real-time eOrder intake and provincial scheduling for vascular imaging, integrated into HEALTHe NL (Orion Health).</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 -mt-10 md:-mt-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Calendar className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Live since Nov 2019</div>
              <div className="text-slate-600">Provincial eOrder program</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <CheckCircle className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">100% adoption</div>
              <div className="text-slate-600">All orders in 4 months</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Network className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">HEALTHe NL</div>
              <div className="text-slate-600">Orion Health portal integration</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Award className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">CHIA 2020 Award</div>
              <div className="text-slate-600">Project Implementation Team</div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50/60 mt-10 md:mt-16 py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">The Challenge</h2>
            <p className="mt-4 text-slate-700">Provincial vascular imaging orders were managed with inconsistent processes and limited visibility—slowing scheduling and preparation.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Orders arriving from multiple channels without a unified workflow</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Coordination of patient prep and lab capacity across sites</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Limited real-time status for labs and requestors</span></li>
            </ul>
          </div>
          <div className="h-60 md:h-full rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
            <div className="text-sm uppercase tracking-wider text-slate-500">Operational Pain Points</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Fragmented intake</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Capacity balancing</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Patient prep</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Status transparency</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Solution</h2>
          <p className="mt-4 text-slate-700">SchedulingCX was integrated with HEALTHe NL to support real-time eOrders, centralized scheduling, and patient preparation for vascular imaging labs. This built on NLCHI’s successful use of Eceptionist for telehealth services dating back to 2009, expanding its role from telehealth management to diagnostic scheduling.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Real-time eOrdering</div><div className="mt-1 text-slate-600">Orders placed via provincial portal flow into SchedulingCX instantly.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Central scheduling</div><div className="mt-1 text-slate-600">Balance capacity across labs; coordinate multi-site schedules.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Patient preparation</div><div className="mt-1 text-slate-600">Automated instructions and readiness tracking.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Audit & insights</div><div className="mt-1 text-slate-600">End-to-end visibility for labs and referring clinicians.</div></div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Results</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>100% of orders</strong> processed within four months of go-live.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Positive lab & clinician feedback</strong> on usability, access to information, and timeliness.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Recognized nationally</strong> with the CHIA 2020 Project Implementation Team of the Year.</span></div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <h2 className="text-2xl md:text-3xl font-semibold">Human Impact</h2>
            <p className="mt-4 text-slate-700">Patients and their circle of care benefit from simpler, faster access to diagnostic vascular imaging—province-wide.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Fewer delays with clear scheduling and prep instructions</span></li>
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Consistent, province-wide experience for clinicians</span></li>
            </ul>
          </div>
          <aside className="rounded-3xl bg-slate-50 ring-1 ring-slate-200 p-6">
            <blockquote className="text-lg leading-relaxed">“The eOrder project made scheduling predictable and transparent for labs and requestors.”</blockquote>
            <div className="mt-3 text-slate-600">— Program Leadership, NLCHI</div>
          </aside>
        </div>
      </section>

      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">Looking Back</h2>
          <p className="mt-4 text-slate-700">SchedulingCX provided a scalable backbone for vascular eOrders and scheduling—leveraging prior HEALTHe NL investments and building on more than a decade of provincial telehealth success with Eceptionist.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Provincial integration</div><div className="text-slate-600 mt-1">Tight link to HEALTHe NL portal</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Rapid adoption</div><div className="text-slate-600 mt-1">Full order coverage in 4 months</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Better utilization</div><div className="text-slate-600 mt-1">Right test, right time, prepared patients</div></div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-tr from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="mx-auto max-w-6xl px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold">Ready to modernize diagnostic ordering and scheduling?</h2>
          <p className="mt-3 text-slate-300">See how EceptionistCX can streamline eOrders, prep, and scheduling across your network.</p>
          <div className="mt-8"><button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 hover:bg-slate-100 transition">Book a Demo</button></div>
        </div>
      </section>

      <div className="h-6" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="Newfoundland & Labrador Case Study"
        pdfPath="/PDF/Detailed Case Studies/Eceptionist_Case+Study_Newfoundalnd.pdf"
      />
    </main>
  )
}

export default CaseStudyNLCHI


